import {
  Alert,
  Button,
  Checkbox,
  Group,
  Paper,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Tabs,
  Text,
  Title,
} from "@mantine/core";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { CHART_COLORS } from "../../theme.js";
import { hasRole, useCurrentUser } from "../../hooks/useAuth.js";
import {
  useMyLeadSegments,
  useSegmentMonthlyKpis,
} from "../../hooks/useKpi.js";
import { useSegments } from "../../hooks/useSegments.js";
import { CapacityDetailPanel } from "../../components/kpi/CapacityDetailPanel.js";
import { TargetsPanel } from "../../components/kpi/TargetsPanel.js";
import { TeamPanel } from "../../components/kpi/TeamPanel.js";
import { TicketBloecke } from "../../components/kpi/tickets/TicketBloecke.js";
import { RECHTE_ACHSE_BREITE } from "../../components/kpi/tickets/ticketBloecke.js";
import { LeadsPanel } from "../../components/kpi/LeadsPanel.js";
import { letzterAbgeschlossenerMonat } from "../../lib/kpiZeitraum";

/**
 * Bereichs-KPIs (Excel „Bereich-KPIs"): Umsatz Soll/Ist/Vorjahr, DB%,
 * Faktura-Stunden Intern/Extern vs. Kapazität. Sichtbar für alle KPI-Rollen
 * (volle Transparenz, Entscheidung Stufe 2).
 */

const MONTH_SHORT = [
  "Jan", "Feb", "Mär", "Apr", "Mai", "Jun",
  "Jul", "Aug", "Sep", "Okt", "Nov", "Dez",
];

const fmtEur = (v: number) => `${Math.round(v).toLocaleString("de-DE")} €`;

export function KpiBereichPage() {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  const { data: segments } = useSegments();
  const currentYear = new Date().getFullYear();
  const [segmentId, setSegmentId] = useState<string | null>(null);
  const [year, setYear] = useState<number>(currentYear);
  // Kontrolliert statt defaultValue — der KPI-Empty-State springt per Button
  // direkt in den Ziele-Tab.
  const [activeTab, setActiveTab] = useState<string | null>("kpis");
  /**
   * Zaehlen Stunden mit, die Team-Mitglieder in ANDEREN Bereichen gebucht
   * haben?
   *
   * Die Soll-Linie ist die Kapazitaet DIESES Teams. Wer im Team steht, aber
   * einen Monat fuer einen anderen Bereich arbeitet, sah ohne den Schalter aus
   * wie eine Luecke: Die Kapazitaet zaehlte, die Leistung nicht. Der Zustand
   * gilt fuer Diagramm UND Team-Tabelle — derselbe Schalter darf an zwei
   * Stellen nicht zwei verschiedene Dinge behaupten.
   *
   * "Extern" bleibt unberuehrt: Das ist Fremdleistung auf den Projekten dieses
   * Bereichs und gehoert nicht zur Team-Leistung.
   */
  const [mitFremden, setMitFremden] = useState(false);

  // Ziele und Team pflegen duerfen nur die Bereichsverantwortlichen — dieselbe
  // Grenze, die der Server beim Schreiben zieht.
  const darfZielePflegen = hasRole(user, [
    "admin",
    "geschaeftsleitung",
    "bereichsleiter",
    "stellvertreter",
  ]);

  /**
   * Wer nur Service Lead ist, sieht ausschliesslich seine eigenen Bereiche.
   *
   * Die Grenze zieht der Server (`SEGMENT_OUT_OF_REACH`); hier steht nur, was
   * davon anklickbar ist. Bei genau einem eigenen Bereich faellt das
   * Auswahlfeld ganz weg — eine Liste mit einem Eintrag ist kein Angebot,
   * sondern eine Behauptung, es gaebe eine Wahl.
   */
  const darfAlleBereiche = hasRole(user, [
    "admin",
    "geschaeftsleitung",
    "bereichsleiter",
    "stellvertreter",
  ]);
  const { data: eigeneBereiche } = useMyLeadSegments(!darfAlleBereiche);
  // useMemo, damit die Vorbelegung unten nicht bei jedem Rendern neu laeuft:
  // Ein frisch erzeugtes Array ist fuer den Abhaengigkeitsvergleich immer neu.
  const waehlbar = useMemo(
    () =>
      darfAlleBereiche
        ? (segments ?? [])
        : (segments ?? []).filter((s) =>
            (eigeneBereiche ?? []).some((e) => e.segmentId === s.id),
          ),
    [darfAlleBereiche, segments, eigeneBereiche],
  );

  // Vorbelegung: eigenes Segment (BL/Stellv.-Rolle bzw. Home-Bereich),
  // sonst der erste waehlbare.
  useEffect(() => {
    if (!segmentId && waehlbar.length > 0) {
      const preferred =
        waehlbar.find((s) => s.id === user?.defaultSegmentId) ?? waehlbar[0]!;
      setSegmentId(preferred.id);
    }
  }, [waehlbar, segmentId, user?.defaultSegmentId]);

  const { data, isLoading, isError } = useSegmentMonthlyKpis(segmentId, year);

  if (isError) {
    return (
      <Alert color="red" title={t("common.error")}>
        {t("common.errorLoading")}
      </Alert>
    );
  }

  const months = data?.months ?? [];
  const ytdActual = months.reduce((a, m) => a + m.revenue.actual, 0);
  const ytdTarget = months.reduce((a, m) => a + (m.revenue.target ?? 0), 0);
  const ytdPrevYear = months.reduce((a, m) => a + m.revenue.prevYear, 0);
  // Bezug fuer die beiden KUMULIERTEN Karten (Wachstum, DB %): der letzte
  // abgeschlossene Monat — nie der laufende. Warum, steht in
  // `lib/kpiZeitraum.ts`; kurz: Der laufende Monat ist in diesem Jahr
  // angefangen und im Vorjahr vollstaendig, das verzerrt den Vergleich um bis
  // zu einen Monatsumsatz. Beide Karten nehmen denselben Monat — zwei Zahlen
  // nebeneinander, die beide „kumuliert" heissen und verschieden weit
  // rechnen, waeren ein neuer Fehler statt der Behebung eines alten.
  const bezugsMonat = letzterAbgeschlossenerMonat(year, new Date());
  const kumuliert = bezugsMonat != null ? months[bezugsMonat] : undefined;
  const internImMonat = (m: (typeof months)[number]) =>
    m.hours.actualIntern +
    (mitFremden
      ? m.hours.actualInternElsewhere + m.hours.actualInternNonService
      : 0);
  const ytdIntern = months.reduce((a, m) => a + internImMonat(m), 0);
  const ytdHoursTarget = months.reduce((a, m) => a + m.hours.target, 0);

  const chartData = months.map((m, i) => ({
    name: MONTH_SHORT[i],
    ist: m.revenue.actual,
    vorjahr: m.revenue.prevYear,
    soll: m.revenue.target ?? undefined,
    dbPct: m.margin.pct ?? undefined,
    dbCumPct: m.margin.cumPct ?? undefined,
    intern: internImMonat(m),
    extern: m.hours.actualExtern,
    stundenSoll: m.hours.target,
  }));

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <Title order={2}>{t("pages.kpi.bereich.title")}</Title>
        <Group gap="sm">
          {waehlbar.length > 1 ? (
            <Select
              value={segmentId}
              data={waehlbar.map((s) => ({ value: s.id, label: s.name }))}
              onChange={setSegmentId}
              style={{ minWidth: 220 }}
            />
          ) : (
            <Text fw={500}>
              {waehlbar.find((s) => s.id === segmentId)?.name ?? ""}
            </Text>
          )}
          <Select
            value={String(year)}
            data={[currentYear - 1, currentYear, currentYear + 1].map(String)}
            onChange={(v) => v && setYear(Number(v))}
            style={{ width: 100 }}
          />
        </Group>
      </Group>

      <Group justify="flex-end">
        <Checkbox
          size="xs"
          label={t("pages.kpi.bereich.mitFremdenBereichen")}
          description={t("pages.kpi.bereich.mitFremdenBereichenHinweis")}
          checked={mitFremden}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setMitFremden(e.currentTarget.checked)
          }
        />
      </Group>

      <Tabs value={activeTab} onChange={setActiveTab} keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="kpis">{t("pages.kpi.tabs.kpis")}</Tabs.Tab>
          <Tabs.Tab value="leads">{t("pages.kpi.tabs.leads")}</Tabs.Tab>
          <Tabs.Tab value="ziele">{t("pages.kpi.tabs.ziele")}</Tabs.Tab>
          {/*
            Der Team-Reiter ist reine Pflege — fuer einen Service Lead steckt
            dahinter nichts Bedienbares. Ein Reiter, hinter dem nur gesperrte
            Felder liegen, ist eine Tuer ohne Raum (Rueckmeldung Hendrik,
            2026-08-20). Die Ziele bleiben ihm, aber lesend: Sie sind der
            Bezugspunkt seiner eigenen Zahlen.
          */}
          {darfZielePflegen && (
            <Tabs.Tab value="team">{t("pages.kpi.tabs.team")}</Tabs.Tab>
          )}
        </Tabs.List>

        <Tabs.Panel value="leads" pt="md">
          {segmentId && <LeadsPanel segmentId={segmentId} year={year} />}
        </Tabs.Panel>

        <Tabs.Panel value="ziele" pt="md">
          {segmentId && (
            <TargetsPanel
              segmentId={segmentId}
              year={year}
              nurLesend={!darfZielePflegen}
            />
          )}
        </Tabs.Panel>

        {darfZielePflegen && (
          <Tabs.Panel value="team" pt="md">
            {segmentId && <TeamPanel segmentId={segmentId} />}
          </Tabs.Panel>
        )}

        <Tabs.Panel value="kpis" pt="md">
      {isLoading ? (
        <Skeleton height={400} />
      ) : ytdActual === 0 && ytdTarget === 0 && ytdPrevYear === 0 ? (
        // Leerer Bereich sieht sonst „kaputt" aus — erklären statt 0er-Charts.
        <Paper p="xl" withBorder>
          <Stack align="center" gap="sm">
            <Text fw={600}>{t("pages.kpi.bereich.emptyTitle")}</Text>
            <Text c="dimmed" size="sm" ta="center" maw={480}>
              {t("pages.kpi.bereich.emptyBody")}
            </Text>
            <Button variant="light" onClick={() => setActiveTab("ziele")}>
              {t("pages.kpi.bereich.emptyCta")}
            </Button>
          </Stack>
        </Paper>
      ) : (
        <>
          <SimpleGrid cols={{ base: 2, md: 5 }} mb="md">
            <StatCard
              label={t("pages.kpi.bereich.ytdActual")}
              value={fmtEur(ytdActual)}
            />
            <StatCard
              label={t("pages.kpi.bereich.targetReach")}
              value={
                ytdTarget > 0
                  ? `${((ytdActual / ytdTarget) * 100).toFixed(1)} %`
                  : "–"
              }
            />
            <StatCard
              label={t("pages.kpi.bereich.growthYtd")}
              value={
                kumuliert?.revenue.growthCumPct != null
                  ? `${kumuliert.revenue.growthCumPct > 0 ? "+" : ""}${kumuliert.revenue.growthCumPct.toFixed(1)} %`
                  : "–"
              }
              color={
                kumuliert?.revenue.growthCumPct == null
                  ? undefined
                  : kumuliert.revenue.growthCumPct >= 0
                    ? "green.7"
                    : "red.7"
              }
              hint={
                bezugsMonat != null
                  ? t("pages.kpi.bereich.growthYtdHint", {
                      period: `${MONTH_SHORT[0]}–${MONTH_SHORT[bezugsMonat]}`,
                      prevYear: year - 1,
                    })
                  : undefined
              }
            />
            <StatCard
              label={t("pages.kpi.bereich.marginCum")}
              value={
                kumuliert?.margin.cumPct != null
                  ? `${kumuliert.margin.cumPct.toFixed(1)} %`
                  : "–"
              }
            />
            <StatCard
              label={t("pages.kpi.bereich.utilizationYtd")}
              value={
                ytdHoursTarget > 0
                  ? `${((ytdIntern / ytdHoursTarget) * 100).toFixed(1)} %`
                  : "–"
              }
            />
          </SimpleGrid>

          <ChartCard title={t("pages.kpi.bereich.revenueChart")}>
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis tickFormatter={(v: number) => `${Math.round(v / 1000)}k`} />
              <YAxis yAxisId="platz" orientation="right" width={RECHTE_ACHSE_BREITE} tick={false} axisLine={false} />
              <Tooltip formatter={(v) => fmtEur(Number(v))} />
              <Legend />
              <Bar dataKey="ist" name={t("pages.kpi.bereich.seriesActual")} fill={CHART_COLORS.ist} />
              <Bar dataKey="vorjahr" name={t("pages.kpi.bereich.seriesPrevYear")} fill={CHART_COLORS.vorjahr} />
              <Line
                dataKey="soll"
                name={t("pages.kpi.bereich.seriesTarget")}
                stroke={CHART_COLORS.soll}
                strokeWidth={2}
                dot={false}
                type="monotone"
              />
            </ComposedChart>
          </ChartCard>

          <ChartCard title={t("pages.kpi.bereich.marginChart")}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis unit=" %" />
              <YAxis yAxisId="platz" orientation="right" width={RECHTE_ACHSE_BREITE} tick={false} axisLine={false} />
              <Tooltip formatter={(v) => `${Number(v).toFixed(1)} %`} />
              <Legend />
              <Line
                dataKey="dbPct"
                name={t("pages.kpi.bereich.seriesMargin")}
                stroke={CHART_COLORS.ist}
                dot
                type="monotone"
              />
              <Line
                dataKey="dbCumPct"
                name={t("pages.kpi.bereich.seriesMarginCum")}
                stroke={CHART_COLORS.positiv}
                strokeWidth={2}
                dot={false}
                type="monotone"
              />
              {data?.marginTargetPct != null && (
                <ReferenceLine
                  y={data.marginTargetPct}
                  stroke={CHART_COLORS.soll}
                  strokeDasharray="4 4"
                />
              )}
            </LineChart>
          </ChartCard>

          <ChartCard title={t("pages.kpi.bereich.hoursChart")}>
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis unit=" h" />
              <YAxis yAxisId="platz" orientation="right" width={RECHTE_ACHSE_BREITE} tick={false} axisLine={false} />
              <Tooltip />
              <Legend />
              <Bar
                dataKey="intern"
                stackId="h"
                name={t("pages.kpi.bereich.seriesIntern")}
                fill={CHART_COLORS.ist}
              />
              <Bar
                dataKey="extern"
                stackId="h"
                name={t("pages.kpi.bereich.seriesExtern")}
                fill={CHART_COLORS.warnung}
              />
              <Line
                dataKey="stundenSoll"
                name={t("pages.kpi.bereich.seriesHoursTarget")}
                stroke={CHART_COLORS.soll}
                strokeWidth={2}
                dot={false}
                type="monotone"
              />
            </ComposedChart>
          </ChartCard>

          {segmentId && (
            <CapacityDetailPanel
              segmentId={segmentId}
              year={year}
              mitFremden={mitFremden}
            />
          )}
        </>
      )}
      {segmentId && (
        <Stack mt="md">
          <TicketBloecke segmentId={segmentId} year={year} />
        </Stack>
      )}
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

function StatCard({
  label,
  value,
  color,
  hint,
}: {
  label: string;
  value: string;
  /** Faerbt den Wert — genutzt fuer Wachstum: rot bei Rueckgang. */
  color?: string;
  /** Kleine Zeile unter dem Wert, z. B. der verglichene Zeitraum. */
  hint?: string;
}) {
  return (
    <Paper p="md" withBorder>
      <Text size="xs" c="dimmed" tt="uppercase">
        {label}
      </Text>
      <Text size="xl" fw={700} c={color}>
        {value}
      </Text>
      {hint && (
        <Text size="xs" c="dimmed">
          {hint}
        </Text>
      )}
    </Paper>
  );
}

function ChartCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactElement;
}) {
  return (
    <Paper p="md" withBorder>
      <Text fw={600} size="sm" mb="xs">
        {title}
      </Text>
      <ResponsiveContainer width="100%" height={280}>
        {children}
      </ResponsiveContainer>
    </Paper>
  );
}
